// RichTextEditor — a rendszer GLOBÁLIS WYSIWYG-je (üzenet-törzs · ticket-leírás · külső komment), TipTap 3-ra.
// Controlled: `value` (HTML-string) + `onChange(html)`. A design `input-glass` keret-mintáját követi (mint az
// Input/Textarea), `disabled`-támogatással.
//
// ══ A TOOLBAR TELJES KÉSZLETE (a legacy CRM WYSIWYG-jének paritása) ═════════════════════════════════════════
//   blokk-típus (bekezdés / 3 címsor) · félkövér · dőlt · aláhúzott · áthúzott · kiemelés · soron belüli kód
//   szöveg-szín (paletta) · betűméret (fix lista) · igazítás (bal/közép/jobb/sorkizárt)
//   felsorolás · számozott lista · idézet · kód-blokk · elválasztó vonal
//   link (beviteli panel) · link eltávolítása · kép (opcionális) · visszavonás/újra · formázás törlése
//
// A előtti készlet CSAK félkövér/dőlt/2 lista/link/kép volt — a legacy ennél sokkal többet tudott
// (aláhúzás, áthúzás, kiemelés, szín, méret, igazítás, idézet, kód, elválasztó), és a felhasználó ezt jelezte.
//
// ══ AMI SZÁNDÉKOSAN KIMARADT a legacy-ből ═══════════════════════════════════════════════════════════════════
//   • BETŰTÍPUS-váltó: a felület egyetlen típus-rendszerre épül (Outfit) — a szabad betűtípus a tipográfiát
//     törné, és e-mailben/exportban kiszámíthatatlan.
//   • SZINTAXIS-KIEMELT kód-blokk (lowlight): a `<pre><code>` megvan, a nyelv-felismerés viszont külön
//     dependency + téma lenne, és a belső kommunikációban nincs rá igény (YAGNI).
//   • VIDEÓ-beágyazás (a legacy saját `iframe`-node-ja): idegen `iframe` a tárolt HTML-ben biztonsági felület
//     (a szanitáló ki is vágná) — a Meet/Zoom hivatkozásra a három dedikált link-mező van.
//
// ══ A HTML-KÉSZLET ÉS A SZANITÁLÁS ═════════════════════════════════════════════════════════════════════════
// A toolbar CSAK olyan formázást kínál, ami a KÖZÖS `@atlas/shared` `rich-text-policy`-t (tag + attribútum +
// CSS-tulajdonság allowlist) túléli — különben a felhasználó formázna, és a mentés/megjelenítés némán elhagyná
// (pontosan ez történt korábban az áthúzással/idézettel/kóddal: a web-render listájából hiányoztak).
// A KIMENET nyers HTML; a megjelenítés MINDIG a `RichTextContent`-en (DOMPurify) megy át (stored-XSS fal), a
// BFF pedig írás előtt is szanitál.
//
// KÉPFELTÖLTÉS (a legacy „Add image" gombjának párja): a gomb egy rejtett `<input type=file>`-t nyit, a
// feltöltés HÁROM lépés (upload-url → közvetlen PUT a tárba → commit), és a HTML-be a commit STABIL URL-je
// kerül — SOHA nem a presigned URL, mert az lejár, és a mentett tartalom képe eltörne. Részletek:
// `apps/api/src/editor-images/editor-images.controller.ts`.
import { EDITOR_IMAGE_TYPES, EDITOR_VIDEO_TYPES } from '@atlas/contracts';
import { RICH_TEXT_COLORS, RICH_TEXT_FONT_SIZES } from '@atlas/shared';
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  Bars3BottomLeftIcon,
  Bars3BottomRightIcon,
  Bars3Icon,
  Bars4Icon,
  BoldIcon,
  ChatBubbleBottomCenterTextIcon,
  ChevronDownIcon,
  CodeBracketIcon,
  CodeBracketSquareIcon,
  ItalicIcon,
  LinkIcon,
  LinkSlashIcon,
  ListBulletIcon,
  MinusIcon,
  NumberedListIcon,
  PaintBrushIcon,
  PhotoIcon,
  VideoCameraIcon,
  StrikethroughIcon,
  SwatchIcon,
  UnderlineIcon,
} from '@heroicons/react/24/outline';
import { Node } from '@tiptap/core';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { Color, FontSize, TextStyle } from '@tiptap/extension-text-style';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { resolveEditorImageSrc, type EditorImageResolverConfig } from '@/lib/editor-image-src';
import { uploadEditorImage, type EditorImageUploadResult } from '@/lib/editor-image-upload';
import { cn } from '@/lib/utils';

/**
 * A címsor-szintek, amiket a szerkesztő kínál, a hozzájuk tartozó (TÍPUSOS) i18n-kulccsal. `h1` NINCS: a
 * lap-címet a felület adja, egy törzsbeli `h1` a dokumentum-hierarchiát törné (lásd a közös policy-t).
 * A kulcs EXPLICIT (nem template-literál + `as`): így a fordítás-kulcs típus-ellenőrzött marad.
 */
const HEADINGS = [
  { level: 2, labelKey: 'richText.heading2' },
  { level: 3, labelKey: 'richText.heading3' },
  { level: 4, labelKey: 'richText.heading4' },
] as const;
const HEADING_LEVELS = [2, 3, 4] as const;

/** Elfogadott link-URL: http(s) — a `mailto:`/`tel:` beírható, de a gyors-ellenőrzés a web-linkre szól. */
const LINK_URL_RE = /^(?:https?:\/\/|mailto:|tel:)\S+$/i;

/**
 * A beágyazott kép node-view-ja. A DOKUMENTUMBAN a STABIL út marad (`/editor-images/:id/content`) — ezt szerializálja
 * a `getHTML()`, tehát ez kerül a mentett tartalomba. A MEGJELENÍTETT `<img>` viszont friss presigned URL-t kap,
 * mert a böngésző a BFF-utat nem tudja betölteni (nem küld `Authorization` fejlécet).
 *
 * MIÉRT NODE-VIEW és nem DOM-foltozás: a ProseMirror a csomópontot az attribútumaiból rendereli újra — egy kézzel
 * átírt `src` a következő újra-renderelésnél visszaállna, a presigned URL pedig bekerülhetne a mentett HTML-be.
 */
const EditorImage = Image.extend<{
  inline: boolean;
  allowBase64: boolean;
  imageResolver: EditorImageResolverConfig | undefined;
}>({
  addOptions() {
    // A FELOLDÓT EXTENSION-OPCIÓKÉNT kapjuk, nem propként: a node-view a tiptap-példányból fut, a React
    // propokhoz nem jut hozzá. A PARTNER-felület emiatt tudja beadni a SAJÁT, szűk feloldóját — a belső
    // végpont neki 403 (és jó okkal: az bármely fájl-id-t feloldaná).
    //
    // A szülő-opciók (`inline`/`allowBase64`) a tiptap `Image`-éből jönnek, és ott OPCIONÁLISAK; a mi
    // opció-típusunk viszont kötelezőnek deklarálja őket (a `configure` mindkettőt megadja), ezért a
    // kiterjesztésnél explicit alapértéket adunk — cast helyett.
    const parent = this.parent?.();
    return {
      inline: parent?.inline ?? false,
      allowBase64: parent?.allowBase64 ?? false,
      imageResolver: undefined,
    };
  },
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('img');
      const src = typeof node.attrs.src === 'string' ? node.attrs.src : '';
      const alt = typeof node.attrs.alt === 'string' ? node.attrs.alt : '';
      dom.alt = alt;
      dom.className = 'max-w-full rounded-md';
      const injected = this.options.imageResolver;
      // A feloldásig NINCS `src` (különben 401-es kép-kérés indulna a BFF-re).
      void resolveEditorImageSrc(src, injected?.resolve, injected?.scope).then((url) => {
        if (url !== null) dom.setAttribute('src', url);
      });
      return { dom };
    };
  },
});

/**
 * VIDEÓ-NODE — rövid képernyőfelvételek beszúrása (user-kérés).
 *
 * MIÉRT SAJÁT NODE, ÉS NEM CSOMAG: a tiptap nem ad videó-node-ot, a feladat viszont szűk — egy atomi blokk,
 * ami `<video src controls>`-ra szerializál. Egy általános „media embed" csomag idegen szolgáltatók
 * (YouTube/Vimeo) `iframe`-jeit is hozná, márpedig a `iframe` a tárolt tartalomban TILOS (harmadik fél +
 * script). Itt a `src` ugyanaz a saját, stabil út, mint a képnél, és a szanitáló ugyanarra a mintára szűr.
 *
 * A NODE-VIEW ugyanazt teszi, mint a képnél: a stabil utat FRISS presigned URL-re oldja fel (a böngésző a
 * stabil utat nem tudja betölteni), és a feloldásig NINCS `src` — különben 401-es kérés indulna a BFF-re.
 */
const EditorVideo = Node.create<ImageResolverOnlyOptions>({
  name: 'editorVideo',
  group: 'block',
  atom: true,
  draggable: true,
  addOptions() {
    return { imageResolver: undefined };
  },
  addAttributes() {
    return {
      src: { default: null },
      // A `controls`/`preload` a MEGJELENÍTÉS dolga — a szanitáló amúgy is kikényszeríti mindkettőt, tehát
      // nem tartjuk attribútumként (a tárolt HTML így nem hordoz redundáns, hamisítható jelzőt).
      alt: { default: null },
    };
  },
  parseHTML() {
    // A TÁROLT alakból visszaolvasás: a fájlnév a `title`-ben van (lásd `renderHTML`).
    return [
      {
        tag: 'video[src]',
        // Az `el` már `HTMLElement` (a tiptap tipizálja) — cast nélkül.
        getAttrs: (el) => ({ src: el.getAttribute('src'), alt: el.getAttribute('title') }),
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const src = typeof HTMLAttributes.src === 'string' ? HTMLAttributes.src : '';
    const alt = typeof HTMLAttributes.alt === 'string' ? HTMLAttributes.alt : '';
    // A SZERIALIZÁLT alak: `controls` + `preload="metadata"` (a szanitáló is ezt kényszeríti). A fájlnév a
    // `title`-ben megy, mert az `alt` nem érvényes attribútum a `<video>`-n (a szanitáló levágná).
    return ['video', { src, controls: '', preload: 'metadata', ...(alt ? { title: alt } : {}) }];
  },
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('video');
      dom.controls = true;
      dom.preload = 'metadata';
      const alt = typeof node.attrs.alt === 'string' ? node.attrs.alt : '';
      if (alt !== '') dom.title = alt;
      dom.className = 'max-w-full rounded-md';
      const src = typeof node.attrs.src === 'string' ? node.attrs.src : '';
      const injected = this.options.imageResolver;
      void resolveEditorImageSrc(src, injected?.resolve, injected?.scope).then((url) => {
        if (url !== null) dom.setAttribute('src', url);
      });
      return { dom };
    };
  },
});

/** A videó-node egyetlen opciója — ugyanaz a feloldó-alak, mint a kép-extensionön. */
interface ImageResolverOnlyOptions {
  imageResolver: EditorImageResolverConfig | undefined;
}

export interface RichTextEditorProps {
  /** A szerkesztő tartalma HTML-ként (controlled). Üres → üres bekezdés. */
  value: string;
  /** Minden szerkesztésnél a friss HTML-lel hívódik. */
  onChange: (html: string) => void;
  /** A szerkesztő elhagyásakor (blur) — a react-hook-form `onTouched` módjához. */
  onBlur?: (() => void) | undefined;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  /** A contenteditable régió aria-label-je (a mezőt leíró szöveg — kötelező akadálymentesítés). */
  'aria-label'?: string | undefined;
  /**
   * A beszúrt kép CÉL-FIÓKJA — OPCIONÁLIS „preferált tenant" (a cél-ügyfelet a BFF oldja fel belőle; a tenant
   * SOHA nem kliens-input a szó szoros értelmében: a fiók id-jéből a szerver keresi ki az ügyfelet).
   *
   * MEGADVA (jegy-form kiválasztott fiókkal): a kép az ADOTT ÜGYFÉL fájl-terébe kerül.
   * ELHAGYVA / üres: a BFF a BELSŐ (MORGENS) ügyfél-rekordot használja — a kép-gomb tehát MINDIG működik.
   *
   * ELŐTT ez háromállapotú kapu volt (`string` = aktív · `null` = tiltott · `undefined` = a gomb
   * meg sem jelenik). A user-lelet ez volt: az üzenet-írásban nem is látszott a gomb, a jegy-formon pedig
   * fiók-választás ELŐTT tiltott volt — mindkettő „a képfeltöltés nem működik"-ként jelent meg. A tenant-kérdést
   * a szerver dönti el (belső ügyfél a fallback), a felületnek nem kell tiltania.
   */
  imageAccountId?: string | null | undefined;
  /**
   * A BEÁGYAZOTT képek megjelenítés-feloldója. Elhagyva a BELSŐ végpont (`/editor-images/:fileId/url`).
   *
   * MIÉRT INJEKTÁLHATÓ: a PARTNER-felület ugyanezt a tárolt HTML-t szerkeszti, de a belső feloldót nem
   * hívhatja — az BÁRMELY fájl-id-t feloldaná, tehát idegen jegyek képei próbálhatók lennének. A partner a
   * saját, SZŰK feloldóját adja be (a jegyhez kötött végpont, illetve a most feltöltött kép seedelt URL-je).
   * Ugyanaz az alak, mint a `RichTextContent` `imageResolver`-e — egy fogalom, egy típus.
   */
  imageResolver?: EditorImageResolverConfig | undefined;
  /**
   * A kép-gomb FELTÖLTŐJE. Elhagyva a belső háromlépéses folyamat (`uploadEditorImage`).
   *
   * MIÉRT INJEKTÁLHATÓ: a partner-oldali feltöltés MÁS végpontokra megy (`/portal/editor-images/*`), ahol a
   * cél-fiók KÖTELEZŐ és a tenant a hívó portál-hozzáféréséből oldódik fel — a belső út „fiók nélkül a
   * MORGENS ügyfél a gazda" fallbackja a partnernél elvi hiba lenne.
   */
  uploadImage?: ((file: File) => Promise<EditorImageUploadResult>) | undefined;
  /**
   * Kinyitás után a KURZOR a szerkesztőbe kerül (a szöveg végére). Az ÖSSZECSUKOTT composerekhez kell: ott a
   * felhasználó egy sávra kattint, és azonnal írni akar — egy plusz kattintás a mezőbe fölösleges lépés.
   * Alapból KI: a lapon már ott lévő szerkesztők nem ragadhatják el a fókuszt a lap-betöltéskor.
   */
  autoFocus?: boolean | undefined;
  className?: string | undefined;
}

/** Egy toolbar-gomb — aktív (formázás bekapcsolt) állapoton kiemelt háttér; a11y: `aria-pressed`. */
function ToolbarButton({
  onClick,
  active,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} // a fókusz a szerkesztőn maradjon (ne ugorjon a gombra)
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active ?? false}
      title={label}
      className={cn(
        'ease-atlas inline-flex size-8 items-center justify-center rounded-md transition-colors duration-150',
        'text-muted-foreground hover:bg-accent hover:text-foreground',
        'disabled:pointer-events-none disabled:opacity-50',
        active && 'bg-accent text-foreground',
      )}
    >
      {children}
    </button>
  );
}

/** Toolbar-elválasztó (vizuális csoportok között). */
function ToolbarSep(): JSX.Element {
  return <span className="bg-border mx-1 h-5 w-px" aria-hidden="true" />;
}

/** Legördülő toolbar-menü (blokk-típus · szín · betűméret) — Popover, hogy a Sheeten belül is működjön. */
function ToolbarMenu({
  label,
  icon,
  disabled,
  children,
}: {
  label: string;
  icon: ReactNode;
  disabled?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          disabled={disabled}
          aria-label={label}
          title={label}
          className={cn(
            'ease-atlas inline-flex h-8 items-center gap-0.5 rounded-md px-1.5 transition-colors duration-150',
            'text-muted-foreground hover:bg-accent hover:text-foreground',
            'disabled:pointer-events-none disabled:opacity-50',
          )}
        >
          {icon}
          <ChevronDownIcon className="size-3" />
        </button>
      </PopoverTrigger>
      {/* portal={false}: modális Sheet/Dialog belsejében a menü görgethető/kattintható maradjon. */}
      <PopoverContent align="start" portal={false} className="w-auto p-1">
        {children}
      </PopoverContent>
    </Popover>
  );
}

/** Egy menü-elem (szöveges) — a legördülőkben. */
function MenuItem({
  onClick,
  active,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'hover:bg-accent flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm',
        active && 'bg-accent text-foreground',
      )}
    >
      {children}
    </button>
  );
}

/**
 * A vágólapról beillesztett KÉPERNYŐKÉP neve a böngészőben generikus (`image.png`) vagy üres — a melléklet-név és a
 * kép `alt`-ja így semmitmondó lenne. Ilyenkor időbélyeges nevet kap (`kepernyokep-20261028-143005.png`); a valódi
 * fájlnév (pl. Finderből másolt kép) változatlan marad.
 */
export function withMediaName(file: File, now: Date = new Date()): File {
  const generic = file.name === '' || /^image\.(png|jpe?g|gif|webp)$/i.test(file.name);
  if (!generic) return file;
  const subtype = file.type.split('/')[1] ?? 'png';
  const ext = subtype === 'jpeg' ? 'jpg' : subtype;
  const stamp = now.toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '-');
  return new File([file], `kepernyokep-${stamp}.${ext}`, { type: file.type });
}

export function RichTextEditor({
  value,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  'aria-label': ariaLabel,
  imageAccountId,
  imageResolver,
  uploadImage,
  autoFocus = false,
  className,
}: RichTextEditorProps): JSX.Element {
  const { t } = useTranslation();

  /**
   * A kép-/videó-beszúrás AKTUÁLIS függvénye. A beillesztés- és a behúzás-kezelő a szerkesztő LÉTREHOZÁSAKOR rögzül
   * (`editorProps`), a beszúrás viszont a mindenkori `editor`-példányt és a propokat használja — a ref köti össze.
   */
  const insertMediaRef = useRef<((file: File) => Promise<void>) | null>(null);

  /**
   * BEILLESZTÉS (Ctrl/Cmd+V) és BEHÚZÁS: a vágólap / húzás FÁJLJAI. Kép és videó → ugyanaz a feltöltés, mint a „Kép"
   * gombé (a névtelen képernyőkép időbélyeges nevet kap); más fájl → jelzés, hogy mellékletként csatolják. `true` =
   * mi kezeltük (a böngésző alapja — egy Finderből másolt képnél a FÁJLNÉV szövegként — nem fut le); `false` =
   * nincs fájl, a sima szöveg / HTML a ProseMirror szokásos beillesztésén megy (kolléga-észrevétel #90 + a jegyes
   * „printscreen" kérés).
   */
  const handleMediaFiles = (files: File[]): boolean => {
    if (disabled || files.length === 0) return false;
    const media = files.filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (media.length < files.length) toast.info(t('richText.pasteNotMedia'));
    if (media.length === 0) return true;
    void (async () => {
      for (const file of media) await insertMediaRef.current?.(withMediaName(file));
    })();
    return true;
  };

  const editor = useEditor({
    extensions: [
      // A StarterKit saját Link-jét kikapcsoljuk, és explicit, biztonságosra konfigurált Link-et adunk.
      // A címsor-szintek a policy-hoz igazítva (h2–h4) — `h1` nincs.
      StarterKit.configure({ link: false, heading: { levels: [...HEADING_LEVELS] } }),
      // KÉP: `inline: false` (blokk-szintű, mint a legacy beszúrása), és a `src` a SAJÁT, stabil utunk.
      // A megjelenítés-oldali fal továbbra is a DOMPurify (`RichTextContent`).
      EditorImage.configure({
        inline: false,
        allowBase64: false,
        ...(imageResolver !== undefined ? { imageResolver } : {}),
      }),
      EditorVideo.configure(imageResolver !== undefined ? { imageResolver } : {}),
      // SZÖVEG-STÍLUS: a `TextStyle` a hordozó mark (`<span style>`), a `Color`/`FontSize` erre épül. A
      // szanitáló CSAK a `color`/`font-size` tulajdonságot engedi át, fix érték-alakokkal.
      TextStyle,
      Color,
      FontSize,
      // KIEMELÉS: EGY tónus (`multicolor: false`) → `<mark>` attribútum nélkül. Szándékos: a többszínű kiemelés
      // `data-color`/inline stílust írna, amit a szanitáló amúgy is levágna.
      Highlight.configure({ multicolor: false }),
      // IGAZÍTÁS: csak bekezdésre és címsorra (a lista-igazítás a legacy-ben sem volt) → `style="text-align:…"`.
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({
        openOnClick: false, // a szerkesztőben ne navigáljon el kattintásra
        autolink: true,
        defaultProtocol: 'https',
        // Protokoll-allowlist a szerkesztéskor is (a render-oldali DOMPurify a végső fal).
        protocols: ['http', 'https', 'mailto', 'tel'],
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
    ],
    content: value,
    editable: !disabled,
    // A tiptap saját autofókusza (`'end'` = a tartalom végére) — így nem kell külön `focus()`-effekt.
    ...(autoFocus ? { autofocus: 'end' as const } : {}),
    editorProps: {
      handlePaste: (_view, event) => handleMediaFiles(Array.from(event.clipboardData?.files ?? [])),
      // a szerkesztőn BELÜLI húzás (`moved`) a ProseMirror saját áthelyezése — azt nem vesszük el
      handleDrop: (_view, event, _slice, moved) =>
        moved ? false : handleMediaFiles(Array.from(event.dataTransfer?.files ?? [])),
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        class: cn(
          // `prose` tipográfia + a mező belső padding/min-magasság; a fókusz-outline-t a konténer adja.
          'prose prose-sm dark:prose-invert max-w-none',
          'min-h-[7rem] w-full px-3 py-2 text-sm focus:outline-none',
        ),
      },
    },
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
    onBlur: () => onBlur?.(),
  });

  // Kontrollált szinkron: ha a külső `value` eltér a szerkesztő aktuális HTML-jétől, betöltjük (update-esemény
  // nélkül, hogy ne keletkezzen visszacsatolási hurok). Reseteknél (pl. form.reset) is helyreáll.
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  // A `disabled` prop változása → a szerkesztő szerkeszthetőségének átállítása.
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  /** A rejtett fájl-választó (a „kép" gomb ezt nyitja) + a folyamatban lévő feltöltés jelzése. */
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  /** A LINK-panel állapota: nyitva van-e, és a beírt URL (a meglévő linkből előtöltve). */
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState(false);

  const insertImage = async (file: File): Promise<void> => {
    setUploading(true);
    try {
      // Fiók-kontextus nélkül `undefined` megy → a BFF a BELSŐ (MORGENS) ügyfelet használja gazdának.
      const res = await (uploadImage
        ? uploadImage(file)
        : uploadEditorImage(
            file,
            imageAccountId === null || imageAccountId === '' ? undefined : imageAccountId,
          ));
      if (res.ok && res.url) {
        // A NODE a MIME-típusból dől el: videót a `editorVideo`, képet a `image` node hordoz. Egy közös
        // node (pl. „media") itt csak elrejtette volna, hogy a szerializált tag más — és a szanitáló
        // tag-szinten szűr.
        const isVideo = file.type.startsWith('video/');
        // A KURZOR A KÉP UTÁNI ÚJ BEKEZDÉSBE kerül (`createParagraphNear`), nem a képen marad.
        //
        // MIÉRT: a `setImage` a beszúrt kép-node-ot KIJELÖLVE hagyja (`ProseMirror-selectednode`), és egy
        // kijelölt node-on a következő leütés KICSERÉLI a node-ot — vagyis aki beszúr egy képet, majd
        // azonnal írni kezd (a leggyakoribb sorrend), az CSENDBEN elveszti a most feltöltött képét. Mérve
        // élő stacken: a jegy-leírás mentett HTML-jéből így maradt ki a kép, hibajelzés nélkül.
        // Az új bekezdés egyben a folytatás helye is — a felhasználónak nem kell a kép mellé kattintania.
        // KÉT KÜLÖN ÁG (nem dinamikus metódus-név): a `setImage` és az `insertContent` bemenete más alakú,
        // és a típus-ellenőrzés csak így tud dolgozni.
        if (isVideo) {
          editor
            ?.chain()
            .focus()
            .insertContent({ type: EditorVideo.name, attrs: { src: res.url, alt: file.name } })
            .createParagraphNear()
            .run();
        } else {
          editor
            ?.chain()
            .focus()
            .setImage({ src: res.url, alt: file.name })
            .createParagraphNear()
            .run();
        }
      } else {
        // A HIBA FAJTÁJÁT fordítjuk (típus / méret / általános) — nyers üzenet nem megy a felhasználóhoz.
        toast.error(t(`richText.imageError.${res.error ?? 'failed'}`));
      }
    } finally {
      setUploading(false);
    }
  };

  insertMediaRef.current = insertImage;

  /**
   * A LINK beszúrása a panelből. KORÁBBAN `window.prompt` volt — az a beúszó/modális panelben nem használható
   * (a Radix fókusz-csapda és a `pointer-events` lock mellett a prompt vagy meg sem jelent, vagy a visszatéréskor
   * a KIJELÖLÉS elveszett, tehát a link nem jött létre; a user „a link nem működik"-ként jelentette).
   *
   * Kijelölés NÉLKÜL a link SZÖVEGE maga az URL lesz (különben egy láthatatlan, üres link keletkezne).
   */
  const applyLink = (): void => {
    if (!editor) return;
    const url = linkUrl.trim();
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setLinkOpen(false);
      return;
    }
    if (!LINK_URL_RE.test(url)) {
      setLinkError(true);
      return;
    }
    const { empty } = editor.state.selection;
    if (empty && !editor.isActive('link')) {
      editor
        .chain()
        .focus()
        .insertContent({ type: 'text', text: url, marks: [{ type: 'link', attrs: { href: url } }] })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setLinkOpen(false);
  };

  /** A link-panel megnyitása: a meglévő link URL-jének előtöltésével. */
  const openLinkPanel = (): void => {
    const prev = (editor?.getAttributes('link').href as string | undefined) ?? '';
    setLinkUrl(prev);
    setLinkError(false);
    setLinkOpen(true);
  };

  /**
   * FORMÁZÁS-TÖRLÉS: karakter-formázás (marks) ÉS blokk-típus (címsor/idézet/lista/kód-blokk) vissza
   * bekezdésre.
   *
   * MIÉRT NEM elég a `chain().focus().unsetAllMarks().clearNodes()` (a korábbi alak, ami MÉRVE nem működött):
   * ÜRES kijelöléssel a `clearNodes()` nincs mire alkalmazni (a felhasználó csak beállt a szövegbe), és a
   * `focus()` a szerkesztő SAJÁT selectionjét használja — így a kattintás látszólag semmit nem tett. Ezért: ha
   * a kijelölés ÜRES, előbb a KURZOR BLOKKJÁT jelöljük ki (`selectParentNode`), és arra futtatjuk a törlést.
   * A `setParagraph()` a `clearNodes()` UTÁN explicit is lefut — a lista/idézet-kontextusból így biztosan
   * bekezdés lesz (a `clearNodes` a wrappert emeli ki, a blokk-típust a `setParagraph` fixálja).
   */
  const clearFormatting = (): void => {
    if (!editor) return;
    const chain = editor.chain().focus();
    if (editor.state.selection.empty) chain.selectParentNode();
    chain.unsetAllMarks().clearNodes().setParagraph().run();
  };

  /** Az aktuális blokk-típus címkéje a legördülő triggeréhez. */
  const activeHeading = HEADING_LEVELS.find((l) => editor?.isActive('heading', { level: l }));

  return (
    <div
      className={cn(
        'input-glass overflow-hidden rounded-lg border',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      {/* TOOLBAR — csoportokra bontva, tördelhető (szűk beúszó panelen két sorba fut). */}
      <div className="border-border flex flex-wrap items-center gap-0.5 border-b px-1.5 py-1">
        {/* BLOKK-TÍPUS: bekezdés / címsor 1–3 */}
        <ToolbarMenu
          label={t('richText.blockType')}
          disabled={disabled}
          icon={
            <span className="px-0.5 text-xs font-medium">
              {activeHeading ? `H${String(activeHeading - 1)}` : t('richText.paragraph')}
            </span>
          }
        >
          <MenuItem
            onClick={() => editor?.chain().focus().setParagraph().run()}
            active={editor?.isActive('paragraph')}
          >
            {t('richText.paragraph')}
          </MenuItem>
          {HEADINGS.map(({ level, labelKey }) => (
            <MenuItem
              key={level}
              onClick={() => editor?.chain().focus().toggleHeading({ level }).run()}
              active={editor?.isActive('heading', { level })}
            >
              <span className={level === 2 ? 'text-base font-semibold' : 'text-sm font-semibold'}>
                {t(labelKey)}
              </span>
            </MenuItem>
          ))}
        </ToolbarMenu>
        <ToolbarSep />

        {/* KARAKTER-FORMÁZÁS */}
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleBold().run()}
          active={editor?.isActive('bold')}
          disabled={disabled}
          label={t('richText.bold')}
        >
          <BoldIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          active={editor?.isActive('italic')}
          disabled={disabled}
          label={t('richText.italic')}
        >
          <ItalicIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
          active={editor?.isActive('underline')}
          disabled={disabled}
          label={t('richText.underline')}
        >
          <UnderlineIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleStrike().run()}
          active={editor?.isActive('strike')}
          disabled={disabled}
          label={t('richText.strike')}
        >
          <StrikethroughIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleHighlight().run()}
          active={editor?.isActive('highlight')}
          disabled={disabled}
          label={t('richText.highlight')}
        >
          <PaintBrushIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleCode().run()}
          active={editor?.isActive('code')}
          disabled={disabled}
          label={t('richText.code')}
        >
          <CodeBracketIcon className="size-4" />
        </ToolbarButton>
        <ToolbarSep />

        {/* SZÍN + BETŰMÉRET (fix paletta/lista — lásd a policy-t) */}
        <ToolbarMenu
          label={t('richText.color')}
          disabled={disabled}
          icon={<SwatchIcon className="size-4" />}
        >
          <div className="grid grid-cols-4 gap-1 p-1">
            {RICH_TEXT_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor?.chain().focus().setColor(c).run()}
                aria-label={c}
                title={c}
                className="border-border size-6 rounded-md border"
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <MenuItem onClick={() => editor?.chain().focus().unsetColor().run()}>
            {t('richText.colorNone')}
          </MenuItem>
        </ToolbarMenu>
        <ToolbarMenu
          label={t('richText.fontSize')}
          disabled={disabled}
          icon={<span className="px-0.5 text-xs font-medium">A</span>}
        >
          <MenuItem onClick={() => editor?.chain().focus().unsetFontSize().run()}>
            {t('richText.fontSizeDefault')}
          </MenuItem>
          {RICH_TEXT_FONT_SIZES.map((size) => (
            <MenuItem
              key={size}
              onClick={() => editor?.chain().focus().setFontSize(size).run()}
              active={editor?.isActive('textStyle', { fontSize: size })}
            >
              <span style={{ fontSize: size }}>{size}</span>
            </MenuItem>
          ))}
        </ToolbarMenu>
        <ToolbarSep />

        {/* IGAZÍTÁS */}
        <ToolbarButton
          onClick={() => editor?.chain().focus().setTextAlign('left').run()}
          active={editor?.isActive({ textAlign: 'left' })}
          disabled={disabled}
          label={t('richText.alignLeft')}
        >
          <Bars3BottomLeftIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().setTextAlign('center').run()}
          active={editor?.isActive({ textAlign: 'center' })}
          disabled={disabled}
          label={t('richText.alignCenter')}
        >
          <Bars3Icon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().setTextAlign('right').run()}
          active={editor?.isActive({ textAlign: 'right' })}
          disabled={disabled}
          label={t('richText.alignRight')}
        >
          <Bars3BottomRightIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().setTextAlign('justify').run()}
          active={editor?.isActive({ textAlign: 'justify' })}
          disabled={disabled}
          label={t('richText.alignJustify')}
        >
          <Bars4Icon className="size-4" />
        </ToolbarButton>
        <ToolbarSep />

        {/* BLOKKOK */}
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          active={editor?.isActive('bulletList')}
          disabled={disabled}
          label={t('richText.bulletList')}
        >
          <ListBulletIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          active={editor?.isActive('orderedList')}
          disabled={disabled}
          label={t('richText.orderedList')}
        >
          <NumberedListIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleBlockquote().run()}
          active={editor?.isActive('blockquote')}
          disabled={disabled}
          label={t('richText.blockquote')}
        >
          <ChatBubbleBottomCenterTextIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
          active={editor?.isActive('codeBlock')}
          disabled={disabled}
          label={t('richText.codeBlock')}
        >
          <CodeBracketSquareIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().setHorizontalRule().run()}
          disabled={disabled}
          label={t('richText.horizontalRule')}
        >
          <MinusIcon className="size-4" />
        </ToolbarButton>
        <ToolbarSep />

        {/* LINK — beviteli panel (nem `window.prompt`: a modális panelben az nem használható) */}
        <Popover
          open={linkOpen}
          onOpenChange={(o) => {
            setLinkOpen(o);
            if (!o) setLinkError(false);
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={openLinkPanel}
              disabled={disabled}
              aria-label={t('richText.link')}
              aria-pressed={editor?.isActive('link') ?? false}
              title={t('richText.link')}
              className={cn(
                'ease-atlas inline-flex size-8 items-center justify-center rounded-md transition-colors duration-150',
                'text-muted-foreground hover:bg-accent hover:text-foreground',
                'disabled:pointer-events-none disabled:opacity-50',
                editor?.isActive('link') && 'bg-accent text-foreground',
              )}
            >
              <LinkIcon className="size-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" portal={false} className="w-72 space-y-2 p-2">
            <Input
              autoFocus
              value={linkUrl}
              onChange={(e) => {
                setLinkUrl(e.target.value);
                setLinkError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyLink();
                }
              }}
              placeholder={t('richText.linkPlaceholder')}
              aria-label={t('richText.linkUrl')}
              inputMode="url"
            />
            {linkError && <p className="text-destructive text-xs">{t('richText.linkInvalid')}</p>}
            <div className="flex items-center justify-end gap-1.5">
              {editor?.isActive('link') && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    editor.chain().focus().extendMarkRange('link').unsetLink().run();
                    setLinkOpen(false);
                  }}
                >
                  {t('richText.linkRemove')}
                </Button>
              )}
              <Button type="button" variant="brand" size="sm" onClick={applyLink}>
                {t('richText.linkApply')}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        <ToolbarButton
          onClick={() => editor?.chain().focus().extendMarkRange('link').unsetLink().run()}
          disabled={disabled || !editor?.isActive('link')}
          label={t('richText.unlink')}
        >
          <LinkSlashIcon className="size-4" />
        </ToolbarButton>

        {/* KÉP (a legacy „Add image" gombjának párja): rejtett fájl-választót nyit; feltöltés közben tiltott.
            MINDIG LÁTSZIK ÉS MŰKÖDIK — a cél-tenantot a szerver dönti el (fiók, vagy a belső ügyfél). */}
        <ToolbarButton
          onClick={() => imageInputRef.current?.click()}
          disabled={disabled || uploading}
          label={t('richText.image')}
        >
          <PhotoIcon className="size-4" />
        </ToolbarButton>
        {/* VIDEÓ — rövid képernyőfelvételekhez (user-kérés, 2026-09-09). KÜLÖN gomb és KÜLÖN `accept`, nem
            egy közös „média" gomb: a natív fájl-választó így csak a valóban beszúrható típusokat kínálja, és
            a két művelet plafonja is más (kép 5 MB, videó 25 MB) — egy közös gombnál a felhasználó a hiba
            szövegéből tudta volna meg, mit is választott. */}
        <ToolbarButton
          onClick={() => videoInputRef.current?.click()}
          disabled={disabled || uploading}
          label={t('richText.video')}
        >
          <VideoCameraIcon className="size-4" />
        </ToolbarButton>
        <input
          ref={imageInputRef}
          type="file"
          // A típus-szűrés ITT is (a natív választó szűkítése) — a megkerülhetetlen fal a BFF sémája. A
          // lista a SZERZŐDÉS whitelistjéből szűrve (nem kézzel írt string).
          accept={EDITOR_IMAGE_TYPES.join(',')}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            // A `value` nullázása kell, hogy UGYANAZ a fájl újra kiválasztható legyen.
            e.target.value = '';
            if (file) void insertImage(file);
          }}
        />
        <input
          ref={videoInputRef}
          type="file"
          accept={EDITOR_VIDEO_TYPES.join(',')}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void insertImage(file);
          }}
        />

        {/* ELŐZMÉNY + FORMÁZÁS-TÖRLÉS (a szerkesztés „meta" műveletei). SZÁNDÉKOSAN NEM `ml-auto`: szűk beúszó
            panelen a jobbra-lökés miatt ez a három gomb SAJÁT, majdnem üres sorba került (mérve 3-soros
            toolbar) — így viszont a többivel együtt tördelődik. */}
        <ToolbarSep />
        <span className="flex items-center gap-0.5">
          <ToolbarButton
            onClick={() => clearFormatting()}
            disabled={disabled}
            label={t('richText.clearFormat')}
          >
            <span className="text-xs font-medium">Tx</span>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor?.chain().focus().undo().run()}
            disabled={disabled || !editor?.can().undo()}
            label={t('richText.undo')}
          >
            <ArrowUturnLeftIcon className="size-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor?.chain().focus().redo().run()}
            disabled={disabled || !editor?.can().redo()}
            label={t('richText.redo')}
          >
            <ArrowUturnRightIcon className="size-4" />
          </ToolbarButton>
        </span>
      </div>

      {/* Szerkesztő-régió + üres-állapot placeholder (overlay, nem a tartalom része). */}
      <div className="relative">
        {editor?.isEmpty && placeholder ? (
          <p
            className="text-subtle pointer-events-none absolute left-3 top-2 text-sm"
            aria-hidden="true"
          >
            {placeholder}
          </p>
        ) : null}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

/** A szerkesztő által használt `Editor` típus re-exportja (a fogyasztóknak nem kell TipTap-ot importálniuk). */
export type { Editor };
